// Constants shared by every policy form: which conditions each product can
// check today, the vocabularies the engine accepts, and the shared wording for
// actions, conditions and fallback. Plain data, no React, so the pure helpers
// and tests can import it.

/* ------------------------------------------------------- signal coverage */

export const SIGNAL_OK = "ok";
export const SIGNAL_NOT_REPORTED = "not_reported";

// What each product's sign-in carries, per condition the console offers:
// "ok", "not_reported", or a sentence when it is only partly reported. Time is
// the server's clock, so every product has it. Change one cell here as
// authn-service and sshproxy start sending a signal; the forms follow.
//   network   = signals.sourceIp
//   privilege = the user's directory groups (AD), the target account (SSH) or
//               the role (Database, from the agent)
//   country   = GeoIP of signals.sourceIp (SSH, Database, Apps)
//   risk      = recent MFA push answers (Database only)
const COUNTRY_PARTLY = "a public address no geolocation can place counts as unknown, and this Allow policy won't apply to it";
export const SIGNALS = {
  ad: { identityExclusions: SIGNAL_OK, time: SIGNAL_OK, network: SIGNAL_OK, privilege: SIGNAL_OK, country: SIGNAL_NOT_REPORTED, risk: SIGNAL_NOT_REPORTED },
  ssh: { identityExclusions: SIGNAL_OK, time: SIGNAL_OK, network: SIGNAL_OK, privilege: SIGNAL_OK, country: COUNTRY_PARTLY, risk: SIGNAL_NOT_REPORTED },
  radius: {
    identityExclusions: SIGNAL_OK,
    time: SIGNAL_OK,
    network: "RADIUS reports the source IP for VPN clients only, not for Wi-Fi or switch logins",
    privilege: SIGNAL_OK,
    country: SIGNAL_NOT_REPORTED,
    risk: SIGNAL_NOT_REPORTED,
  },
  database: {
    identityExclusions: SIGNAL_OK,
    time: SIGNAL_OK,
    network: SIGNAL_OK,
    privilege: "it applies only once the database agent has reported the role's privilege (agent update required)",
    country: COUNTRY_PARTLY,
    risk: SIGNAL_OK,
  },
  apps: { identityExclusions: SIGNAL_OK, time: SIGNAL_OK, network: SIGNAL_OK, privilege: SIGNAL_OK, country: COUNTRY_PARTLY, risk: SIGNAL_NOT_REPORTED },
};


// "ok" | "not_reported" | note. Unknown product or condition reads as
// not reported: never claim a signal we have not confirmed.
export const signalFor = (product, condition) => SIGNALS[product]?.[condition] || SIGNAL_NOT_REPORTED;

// How the warning names the product and the signal.
const SIGNAL_PRODUCT_NAME = { ad: "AD", ssh: "SSH", radius: "RADIUS", database: "The database login", apps: "The MFA API" };
const SIGNAL_NAME = { network: "the source IP", privilege: "privilege", time: "the time", country: "the country", risk: "a risk level" };

/**
 * The warning an Allow policy gets for a condition its product can't check
 * (spec 3): "This policy won't apply: SSH doesn't report the source IP." Null
 * when the signal is reported, or the policy isn't Allow (Deny and Require MFA
 * still apply when a condition is unknown).
 */
export function signalWarning(product, condition, flow) {
  if (flow !== "allow") return null;
  const s = signalFor(product, condition);
  if (s === SIGNAL_OK) return null;
  if (s !== SIGNAL_NOT_REPORTED) return `This policy won't always apply: ${s}.`;
  const name = SIGNAL_PRODUCT_NAME[product] || "This product";
  return `This policy won't apply: ${name} doesn't report ${SIGNAL_NAME[condition] || condition}.`;
}

/* ---------------------------------------------------------- vocabularies */
// The engine's own lists (internal/policy/conditions/validate.go). A value
// outside them is refused with a 400, so the chips offer only these.

export const DIRECTORY_OPTIONS = [
  { value: "local", label: "Local" },
  { value: "ad", label: "AD" },
  { value: "entra", label: "Entra ID" },
];

export const AUTH_STRENGTH_OPTIONS = [
  { value: "none", label: "No sign-in yet" },
  { value: "password", label: "Password" },
  { value: "mfa", label: "MFA" },
  { value: "phishing_resistant", label: "Passkey / phishing-resistant" },
];

export const DEVICE_TRUST_OPTIONS = [
  { value: "managed", label: "Managed" },
  { value: "compliant", label: "Compliant" },
  { value: "registered", label: "Registered" },
  { value: "unmanaged", label: "Unmanaged" },
];

export const PLATFORM_OPTIONS = [
  { value: "windows", label: "Windows" },
  { value: "macos", label: "macOS" },
  { value: "linux", label: "Linux" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
];

export const SENSITIVITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export const PRIVILEGE_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "elevated", label: "Elevated" },
  { value: "admin", label: "Admin" },
];

export const RISK_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

// Display order Mon..Sun. The engine's own order (used in its error text) is
// sun..sat: see ENGINE_DAY_ORDER.
export const DAY_OPTIONS = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];
export const ENGINE_DAY_ORDER = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri"];
export const WEEKEND = ["sat", "sun"];

/* --------------------------------------------------------------- actions */

// The grant, in the same words on every product (older screens and services).
export const ACTION_OPTIONS = [
  { value: "mfa_required", label: "Require MFA: push to the user's phone" },
  { value: "allow", label: "Allow" },
  { value: "block", label: "Deny" },
];
export const actionLabel = (flow) => ACTION_OPTIONS.find((o) => o.value === flow)?.label || flow || "";

// The one action control on every product: this order, these words. `tone`
// is how the segmented control colours the selected option (red only for Deny).
export const POLICY_ACTIONS = [
  { value: "allow", label: "Allow", tone: "neutral" },
  { value: "mfa_required", label: "Require MFA", tone: "brand" },
  { value: "block", label: "Deny", tone: "danger" },
];
export const policyActionLabel = (flow) => POLICY_ACTIONS.find((o) => o.value === flow)?.label || "";

// One sentence under the action control. `subject` is the product's word for
// what is being decided ("sign-in", "session", "login").
export const actionSentence = (flow, subject = "sign-in") => {
  if (flow === "allow") return `Allow: the ${subject} goes through with no second factor.`;
  if (flow === "mfa_required")
    return `Require MFA: the user approves a push on their phone before the ${subject} goes through.`;
  if (flow === "block") return `Deny: the ${subject} is refused.`;
  return "";
};

// List groups, in the order the engine checks them.
export const POLICY_GROUPS = [
  { key: "block", label: "Deny", line: "Checked first: a matching Deny always wins." },
  { key: "mfa_required", label: "Require MFA", line: "" },
  { key: "allow", label: "Allow", line: "" },
  { key: "fallback", label: "Fallback", line: "Only when no other policy matches." },
];
// Database only: Require MFA also wins over Allow.
export const MFA_WINS_ALLOW_LINE = "An MFA policy that also matches wins.";

/* ------------------------------------------------------------ advanced */

export const ADVANCED_TITLE = "Only when…";
export const FALLBACK_LABEL = "Use as fallback";
export const FALLBACK_DESC = "Only applies when no other policy matches.";
export const EXCEPT_HELP = "This policy never applies to these users and groups. Use it for break-glass accounts.";
export const broadDenyWarning = (subject = "sign-in") =>
  `This blocks every ${subject} it matches, including ones your other policies allow. To block only what nothing else covers, turn on Use as fallback under Only when.`;

export const HOW_CONDITIONS_WORK = [
  "Every condition you add must hold for the policy to apply.",
  "If a condition doesn't hold, the policy is skipped and the next one is checked. A condition never refuses anyone by itself.",
  "When AuthNull can't tell (for example, the product doesn't report where a sign-in came from), Deny and Require MFA policies still apply and Allow policies don't.",
];

// Privilege, in product terms (spec 3).
const AD_PRIVILEGE_MEANING =
  "Admin = Domain, Enterprise or Schema Admins, or Administrators. Elevated = operator groups (Account, Backup, Server, Print Operators, DnsAdmins and similar).";
export const PRIVILEGE_MEANING = {
  ad: AD_PRIVILEGE_MEANING,
  radius: AD_PRIVILEGE_MEANING,
  apps: AD_PRIVILEGE_MEANING,
  ssh: "Admin = root.",
  database:
    "What the database role can do, as the agent reports it. Admin = superuser, or can run programs or read and write files on the server. Elevated = can create roles or databases, replicate, bypass row-level security, or read and write all data. Standard = anything else.",
};

// Country: "internal" is a private address (office, VPN, jump host), which
// no geolocation can place.
export const COUNTRY_INTERNAL = "internal";
export const COUNTRY_MEANING = "Where the client address is. Private addresses (office, VPN, jump host) count as Internal network, not a country.";
export const RISK_MEANING =
  "From the person's MFA pushes in the last hour: a denied push counts 2, an unanswered one 1. Low = 0–1, Medium = 2–4, High = 5 or more.";

// Shown in AD's Network condition.
export const AD_NETWORK_NOTE = "Best-effort for NTLM sign-ins (for example RDP from a machine that isn't domain-joined).";

// Conditions an admin can add, in menu order. `products`: only these offer it.
// Country and Risk only where the sign-in carries them: a condition that is
// always unknown would make every Deny and Require MFA policy apply.
export const OFFERED_CONDITIONS = [
  { key: "time", label: "Time" },
  { key: "network", label: "Network" },
  { key: "privilege", label: "Privilege" },
  { key: "country", label: "Country", products: ["ssh", "database", "apps"] },
  { key: "risk", label: "Risk", products: ["database"] },
];
export const offeredConditions = (product) => OFFERED_CONDITIONS.filter((c) => !c.products || !product || c.products.includes(product));

// Stored conditions the editor doesn't offer: shown read-only with remove,
// kept on save unless removed.
export const HIDDEN_CONDITIONS = [
  { key: "authStrength", label: "Sign-in strength" },
  { key: "device", label: "Device" },
  { key: "resource", label: "Resource sensitivity" },
  { key: "identity.directories", label: "Directory" },
];
export const SET_OUTSIDE = "set outside the console";
